import { finalize } from 'rxjs';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Guid } from 'guid-typescript';
import { BaseEntityModel } from 'src/app/models/base-entity.model';
import { ClientModel } from 'src/app/models/client.model';
import { ClientService } from 'src/app/services/client.services';
import { ToastService } from 'src/app/services/toast.service';
import { countryCodesList } from 'src/assets/data/countries';
import { MatFormField, MatLabel, MatInput, MatError } from '@angular/material/input';
import { MatSelect, MatOption, MatSelectTrigger } from '@angular/material/select';

import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
    selector: 'app-client',
    templateUrl: './client.component.html',
    styleUrls: ['./client.component.scss'],
    viewProviders: [
        { provide: ControlContainer, useExisting: FormGroupDirective },
    ],
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatSelect, MatOption, MatInput, MatIcon, MatError, MatSelectTrigger, MatCheckbox]
})
export class ClientComponent implements OnInit, AfterViewInit {
  private readonly clientService = inject(ClientService);
  toastr = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly activatedRoute = inject(ActivatedRoute);

  clientForm: UntypedFormGroup = this.createClient('', '');
  countryCodesList: any[] = countryCodesList;
  selectedCode: {} = {};
  selectedType: string = '';
  selectedflag: string = '';
  wasSearched: boolean = false;
  // The DNI is looked up on Enter, on the search button and when leaving the field: skip repeated lookups.
  private isSearching = false;
  private lastSearched: string | null = null;
  identificationTypeList: BaseEntityModel[] = [];
  identificatinoNumberMinLength: number = 6;
  showCheckboxNoMail: boolean = true;
  ngAfterViewInit(): void {
    this.getIdentificationTypes();
  }

  ngOnInit(): void {
    this.selectedflag = this.countryCodesList.find((x) => x.code == 'AR').src;

    this.clientForm.controls.identificationTypeId.setValue(this.selectedType);
    this.disableClientFrom();
    this.clientForm.controls.identificationNumber.valueChanges.subscribe(
      (val: string) => {
        if (this.wasSearched) {
          this.disableClientFrom();
          this.CleanControls();
          this.wasSearched = false;
          this.showCheckboxNoMail = this.clientForm.controls.email.value;
        }
      }
    );

    this.clientForm.controls.identificationTypeId.valueChanges.subscribe(
      (val: string) => {
        if (this.wasSearched) {
          this.disableClientFrom();
          this.CleanControls();
          this.wasSearched = false;
        }
      }
    );
  }

  getIdentificationTypes() {
    this.clientService.getIdentificationTypes().subscribe((data) => {
      this.identificationTypeList = data;
      this.selectedType = this.identificationTypeList.find(
        (x) => x.name == 'DNI'
      )?.id;
      this.clientForm.controls.identificationTypeId.setValue(this.selectedType);
    });
  }

  disableClientFrom() {
    let opportunityId: any = undefined;

    this.clientForm.controls['name'].disable();
    this.clientForm.controls['email'].disable();
    this.clientForm.controls['phone'].disable();
    this.clientForm.controls['allowEmptyEmail'].disable();

    this.activatedRoute.queryParams.subscribe((params) => {
      opportunityId = params['opportunityId'];
    });

    if (opportunityId) {
      this.clientForm.controls['identificationTypeId'].disable();
      this.clientForm.controls['identificationNumber'].disable();
      this.clientForm.controls['name'].disable();
      this.clientForm.controls['email'].disable();
      this.clientForm.controls['phone'].disable();
      this.clientForm.controls['allowEmptyEmail'].disable();
    }
  }

  enableClientFrom() {
    this.clientForm.controls['name'].enable();
    this.clientForm.controls['email'].enable();
    this.clientForm.controls['phone'].enable();
    this.clientForm.controls['allowEmptyEmail'].enable();
  }

  CleanControls() {
    this.clientForm.controls['name'].setValue(
      new UntypedFormControl('', Validators.required).value
    );
    this.clientForm.controls['email'].setValue(
      new UntypedFormControl('', [Validators.required, Validators.email]).value
    );
    this.clientForm.controls['allowEmptyEmail'].reset();
    this.clientForm.controls['phone'].setValue(this.createClientPhone().value);
  }

  changeSelection(countryCode: any) {
    this.selectedflag = this.countryCodesList.find(
      (x) => x.countryCode == countryCode
    ).src;
  }

  changeSelectedType(identificationType: any, clearForm: boolean = true) {
    this.selectedType = this.identificationTypeList.find(
      (x) => x.id == identificationType
    )?.id;
    if (this.selectedType != '3d2fd110-f935-43c1-955d-378e25d7270f') {
      this.identificatinoNumberMinLength = 6;
      this.clientForm.controls.identificationNumber.setValidators([
        Validators.required,
        Validators.pattern('^[0-9]{7,8}$'),
        Validators.minLength(7),
      ]);
    } else {
      this.identificatinoNumberMinLength = 10;
      this.clientForm.controls.identificationNumber.setValidators([
        Validators.required,
        Validators.pattern('^[0-9]{10,11}$'),
        Validators.minLength(11),
      ]);
    }
    if (clearForm) {
      this.clientForm.controls.identificationNumber.setValue(null);
      this.clientForm.controls.identificationNumber.markAsUntouched();
      this.CleanControls();
    }
  }

  createClient(
    identificationNumber: string,
    identificationTypeId: string
  ): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl(Guid.createEmpty().toString()),
      name: new UntypedFormControl('', Validators.required),
      identificationTypeId: new UntypedFormControl(
        identificationTypeId,
        Validators.required
      ),
      identificationNumber: new UntypedFormControl(identificationNumber, [
        Validators.required,
        Validators.pattern('^[0-9]{7,8}$'),
        Validators.minLength(7),
      ]),
      isActive: new UntypedFormControl(false),
      phone: this.createClientPhone(),
      email: new UntypedFormControl('', [
        Validators.required,
        Validators.email,
      ]),
      allowEmptyEmail: new UntypedFormControl(false),
    });
  }

  createClientPhone(): UntypedFormGroup {
    return new UntypedFormGroup({
      countryCode: new UntypedFormControl('+54', [Validators.required]),
      area: new UntypedFormControl(' '),
      number: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]{10,}$'),
      ]),
    });
  }

  /** Phone number is typed as text so leading zeros survive; strip anything that is not a digit, max 10. */
  keepPhoneDigits(event: Event, control: AbstractControl | null) {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 10);
    if (digits !== input.value) {
      input.value = digits;
    }
    control?.setValue(digits, { emitEvent: false });
  }

  validateOnlyNumbers(event: KeyboardEvent) {
    let key = event.key;
    if (['.', 'e', 'E', ',', '-', '+'].includes(key)) {
      event.preventDefault();
    }
  }

  getClient() {
    const rawIdentificationNumber = this.clientForm.controls.identificationNumber.value;
    if (rawIdentificationNumber === null || rawIdentificationNumber === undefined) return;

    let identificationNumber = rawIdentificationNumber.toString();
    let identificationTypeId =
      this.clientForm.controls.identificationTypeId.value;

    if (this.isSearching || (this.wasSearched && this.lastSearched === identificationNumber)) return;

    if (identificationNumber.length > this.identificatinoNumberMinLength) {
      this.isSearching = true;
      this.clientService
        .getClient(identificationNumber, identificationTypeId)
        .pipe(finalize(() => (this.isSearching = false)))
        .subscribe((data: ClientModel) => {
          this.clientForm.controls.identificationTypeId.setValue(
            this.selectedType
          );
          if (data) {
            this.clientForm.patchValue(data);
            this.showCheckboxNoMail = !this.clientForm.controls.email.value;
            if (!data.phone) {
              this.clientForm.markAllAsTouched();
            }
          } else {
            this.clientForm.reset();
            this.clientForm.patchValue(
              this.createClient(identificationNumber, identificationTypeId)
                .value
            );
            this.showCheckboxNoMail = true;
          }
          this.enableClientFrom();
          this.noMailClient();
          this.wasSearched = true;
          this.lastSearched = identificationNumber;
        });
      this.clientForm.controls.identificationNumber.markAsUntouched();
    }
  }

  getClientId(): any {
    return this.clientForm.controls['id'].value;
  }

  getClientEmail(): any {
    return this.clientForm.controls['email'].value;
  }

  validClientForm() {
    let isValid = this.clientForm.valid && this.wasSearched;
    if (!this.wasSearched) {
      this.toastr.warning(
        'Asignar cliente a trabajo',
        'Debe buscar un cliente existente o registrar uno nuevo.'
      );
    }
    if (!this.clientForm.valid) {
      this.clientForm.markAllAsTouched();
    }
    return isValid;
  }

  checkSelectedType(identificationTypesName: string) {
    return (
      this.selectedType ==
      this.identificationTypeList.find((x) => x.name == identificationTypesName)
        ?.id
    );
  }

  noMailClient() {
    if (this.clientForm.controls.allowEmptyEmail.value) {
      this.clientForm.controls.email.setValidators([]);
      this.clientForm.controls['email'].disable();
    } else {
      this.clientForm.controls.email.setValidators([
        Validators.required,
        Validators.email,
      ]);
      this.clientForm.controls['email'].enable();
    }
    this.cdr.detectChanges();
    this.clientForm.controls.email.updateValueAndValidity();
  }
}
